import React, { useMemo } from 'react'; // ✅ เพิ่ม useMemo
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import dayjs from 'dayjs';
import _ from 'lodash';
import { styled } from '@mui/material/styles';
import { addCommas } from '../../utils/functions/commas';
import { CircularProgress } from '@mui/material';

import utc from 'dayjs/plugin/utc';
import SortableTableLabel from '../Table/SortableTableLabel';

dayjs.extend(utc);

const formatStatementDateTime = (value) =>
  value ? dayjs.utc(value).add(7, 'hour').format('YYYY-MM-DD HH:mm:ss.SSS') : '-';

// ❌ ย้าย columns array จากตรงนี้ ไปไว้ข้างใน component
// const columns = [ ... ];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#fff', // หรือ theme.palette.action.hover ถ้าต้องการ
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },

  // [ปรับปรุง] ย้าย :hover มาไว้ที่นี่
  '&:hover': {
    
    boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
    position: 'relative',
    zIndex: 10,
    cursor: 'pointer', // เพิ่ม cursor เพื่อให้รู้ว่า hover ได้
  },


  transition: 'transform 0.15s ',
}));


const StickyTableCell = styled(TableCell)(({ theme }) => ({
  position: 'sticky',
  left: 0,
  backgroundColor: theme.palette.background.paper,
  zIndex: theme.zIndex.appBar, // สำหรับ header ให้สูงกว่า body
}));

export default function TableStatmentOnline({
  statementValue,
  setOpen,
  setValue,
  page,
  total,
  rowsPerPage,
  setRowsPerPage,
  setPage,
  isFetching,
  t, // ✅ เราจะใช้ t ตัวนี้
  isLoading,
  sortBy,
  sortOrder,
  setSortBy,
  setSortOrder,
  // saveQuery,
  // callApiGetStatementOnline,
}) {
  // ✅ ย้าย columns array เข้ามาข้างใน และใช้ useMemo
 const columns = useMemo(
  () => [
    {
      id: 'statement_ref',
      label: t('app.statementRef'),
      minWidth: 90, // ลดลงเล็กน้อย
      align: 'left',
    },
    {
      id: 'bookbank',
      label: t('app.bookbankNo'),
      minWidth: 90, // เลขบัญชี/สาขา มักจะสั้น
      align: 'center',
    },
    {
      id: 'transaction_date',
      label: t('app.dateTime'),
      minWidth: 110, // วันที่ลดเหลือ 110px พอ (ถ้าฟอร์แมตสั้น เช่น DD MMM YY)
      align: 'center',
    },
    {
      id: 'effective_date',
      label: t('app.effectiveStartDate'),
      minWidth: 110,
      align: 'center',
    },
    {
      id: 'amount',
      label: t('app.actualReceived'),
      minWidth: 90, // ตัวเลขสั้นๆ
      align: 'center',
    },
    {
      id: 'debit_amount',
      label: t('app.actualPaid'),
      minWidth: 90,
      align: 'center',
    },
    {
      id: 'new_spend_amount',
      label: t('app.amountUsed'),
      minWidth: 90,
      align: 'center',
    },
    {
      id: 'new_balance',
      label: t('app.balance'),
      minWidth: 90,
      align: 'center',
    },
    {
      id: 'balance',
      label: t('app.broughtForward'),
      minWidth: 90,
      align: 'center',
    },
    {
      id: 'status',
      label: t('app.status'),
      minWidth: 80, // สถานะย่อๆ 
      align: 'center',
    },
    {
      id: 'detail_status',
      label: t('app.detailStatus'),
      minWidth: 140, // ลดจาก 200
      align: 'center',
    },
    {
      id: 'remark',
      label: t('app.remark'),
      minWidth: 140, // ลดจาก 200
      align: 'center',
    },
    {
      id: 'description',
      label: t('app.description'),
      minWidth: 140, // ลดจาก 200
      align: 'center',
    },
    {
      id: 'channel',
      label: t('app.channel'),
      minWidth: 80, // ช่องทางมักเป็นคำสั้นๆ เช่น App, Web, Counter
      align: 'center',
    },
    {
      id: 'customer_code',
      label: t('navbar.custNo'),
      minWidth: 90,
      align: 'center',
    },
    {
      id: 'customer_name',
      label: t('navbar.custName'),
      minWidth: 130, // ชื่อลูกค้าเผื่อไว้นิดนึง
      align: 'center',
    },
    {
      id: 'invoice_no',
      label: t('app.invoiceNo'),
      minWidth: 110, // เลขที่ใบแจ้งหนี้ลดเหลือ 110
      align: 'center',
    },
    {
      id: 'bs_no',
      label: t('app.bsNo'),
      minWidth: 80,
      align: 'center',
    },
    {
      id: 'RC03',
      label: t('app.rc03'),
      minWidth: 80,
      align: 'center',
    },
    {
      id: 'check_no',
      label: t('app.chqNo'),
      minWidth: 80,
      align: 'center',
    },
    {
      id: 'edit_by',
      label: t('app.updateBy'),
      minWidth: 90,
      align: 'center',
    },
    {
      id: 'edit',
      label: t('app.edit'),
      minWidth: 70, // ปุ่มแก้ไขใช้พื้นที่น้อยมาก
      align: 'center',
      sortable: false,
    },
  ],
  [t],
);

  // const onChangePageTable = (newPage) => {
  //   callApiGetStatementOnline({
  //     ...saveQuery,
  //     page: newPage + 1,
  //     size: rowsPerPage,
  //   });
  // };

  // const onChangeRowsPerPageTable = (newRows) => {
  //   callApiGetStatementOnline({
  //     ...saveQuery,
  //     page: 0,
  //     size: newRows,
  //   });
  // };
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleRequestSort = (property) => {
    setSortOrder(
      sortBy === property && sortOrder === 'asc' ? 'desc' : 'asc',
    );
    setSortBy(property);
    setPage(0);
  };

  // console.log('statement', statementValue);
  return (
    <div className="flex-wrap mt-4 border-collapse border border-slate-200 ">
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
     <TableContainer sx={{ overflowX: 'auto', overflowY: 'visible' }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => {
                  // const newValue = [column.id];

                  if (column.id === 'statement_ref') {
                    return (
                      <StickyTableCell
                        sx={{
                          backgroundColor: 'primary.main',
                          color: '#fff',
                          minWidth: 200,
                          zIndex: 1200,
                        }}
                      >
                        <SortableTableLabel
                          column={column}
                          sortBy={sortBy}
                          sortOrder={sortOrder}
                          onSort={handleRequestSort}
                          color="white"
                        />
                      </StickyTableCell>
                    );
                  }

                  return (
                    <StyledTableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                    >
                      <div className="whitespace-nowrap">
                        <SortableTableLabel
                          column={column}
                          sortBy={sortBy}
                          sortOrder={sortOrder}
                          onSort={handleRequestSort}
                          color="white"
                        />
                      </div>
                    </StyledTableCell>
                  );
                })}
                {/* {columns.map((column) => (
                  <StyledTableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                  >
                    <div className="whitespace-nowrap">{column.label}</div>
                  </StyledTableCell>
                ))} */}
              </TableRow>
            </TableHead>

            <TableBody>
              {isFetching ? (
                <TableRow>
                
                  <TableCell align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                  <TableCell> </TableCell>
                </TableRow>
              ) : statementValue.length > 0 ? (
                _.map(statementValue, (item, index) => (
                  <StyledTableRow
                    key={index}
                    hover
                    role="checkbox"
                    tabIndex={-1}
                  >
                    {columns.map((column) => {
                      const value = item[column.id];

                      if (column.id === 'statement_ref') {
                        return (
                          <StickyTableCell key={column.id} align={column.align}>
                            {value}
                          </StickyTableCell>
                        );
                      }
                      if (
                        column.id === 'transaction_date' ||
                        column.id === 'effective_date'
                      ) {
                        return (
                          <TableCell key={column.id} align="center">
                            {formatStatementDateTime(value)}
                          </TableCell>
                        );
                      }
                      if (
                        [
                          'amount',
                          'debit_amount',
                          'new_spend_amount',
                          'new_balance',
                          'balance',
                        ].includes(column.id)
                      ) {
                        return (
                          <TableCell key={column.id} align="center">
                            {value !== 0 ? addCommas(value) : '-'}
                          </TableCell>
                        );
                      }
                      if (column.id === 'edit') {
                        return (
                          <TableCell key={column.id} align={column.align}>
                            <IconButton
                              aria-label="Edit"
                              onClick={() => {
                                setOpen(true);
                                setValue(item);
                              }}
                            >
                              <EditIcon
                                fontSize="small"
                                sx={{ color: 'primary.light' }}
                              />
                            </IconButton>
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell key={column.id} align={column.align}>
                          {value}
                        </TableCell>
                      );
                    })}
                  </StyledTableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    {t('app.noDataAvailable')} {/* ✅ แก้ไข */}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 25, 100]}
          component="div"
          count={isFetching ? 0 : total}
          rowsPerPage={rowsPerPage}
          page={isFetching ? 0 : page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} of ${addCommas(count, 0)}`
          }
        />
      </Paper>
    </div>
  );
}
